import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { DetectionRulesRecordState } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';

export const actions: ActionTree<DetectionRulesRecordState, RootState> = {
    /**
     * 検知記録一覧取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 申請書ID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} searchItems 検索の絞り込み項目
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            start_date: searchItems.startDate,
            end_date: searchItems.endDate,
            word: searchItems.word,
            application_form_number: searchItems.application_form_number,
            application_form_id: searchItems.application_form_id,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.DetectionRulesRecord.SEARCH,
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.SEARCH_FAILURE, params);
        }
    },

    /**
     * 検知記録の詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 検知記録ID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.DetectionRulesRecord.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 検知情報の取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id ページ番号
     * @returns {Promise<void>}
     */
    async logFile({ dispatch, commit }, { id }) {
        try {
            commit(types.LOG_FILE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.DetectionRulesRecord.LOG_FILE.replace(':id', id.toString()),
                    params: { detection_limit: AppContext.DetectionLogs.MAX_ITEMS },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.LOG_FILE_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.LOG_FILE_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} searchItems 検索の絞り込み項目
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { searchItems = {}, sort } = {}) {
        const payload = {
            start_date: searchItems.startDate,
            end_date: searchItems.endDate,
            word: searchItems.word,
            application_form_number: searchItems.application_form_number,
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.DetectionRulesRecord.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CSV_OUTPUT_FAILURE, params);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
